import type { ReactNode } from "react";

/**
 * Minimal, dependency-free markdown renderer for trusted-ish community content
 * (spot guides). Supports ## / ### headings, **bold**, [links](url), and
 * "- " bullet lists. Raw HTML in the source is escaped first so community
 * guides cannot inject markup.
 */
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function parseInline(text: string): string {
  return escapeHtml(text)
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(
      /\[([^\]]+)\]\(([^)]+)\)/g,
      '<a href="$2" class="text-gold underline hover:text-gold-light">$1</a>'
    );
}

export function Markdown({ content }: { content: string }): ReactNode {
  const lines = content.split("\n");
  const elements: ReactNode[] = [];
  let currentList: string[] = [];
  let key = 0;

  function flushList() {
    if (currentList.length > 0) {
      const items = currentList;
      elements.push(
        <ul key={key++} className="list-disc pl-5 mb-4 space-y-1.5">
          {items.map((item, i) => (
            <li key={i} dangerouslySetInnerHTML={{ __html: parseInline(item) }} />
          ))}
        </ul>
      );
      currentList = [];
    }
  }

  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed.startsWith("### ")) {
      flushList();
      elements.push(
        <h4 key={key++} className="font-serif text-[16px] font-bold text-navy mt-5 mb-2">
          {trimmed.slice(4)}
        </h4>
      );
    } else if (trimmed.startsWith("## ")) {
      flushList();
      elements.push(
        <h3 key={key++} className="font-serif text-[18px] md:text-[20px] font-bold text-navy mt-6 mb-2.5 first:mt-0">
          {trimmed.slice(3)}
        </h3>
      );
    } else if (trimmed.startsWith("- ")) {
      currentList.push(trimmed.slice(2));
    } else if (trimmed === "") {
      flushList();
    } else {
      flushList();
      elements.push(
        <p
          key={key++}
          className="mb-3"
          dangerouslySetInnerHTML={{ __html: parseInline(trimmed) }}
        />
      );
    }
  }
  flushList();

  return (
    <div className="text-[14px] leading-[1.75] text-text-body max-w-[720px]">
      {elements}
    </div>
  );
}
